import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { NavigationTab } from '@child-nav/shared';
import { 
  ChildProfile, 
  DevelopmentAge, 
  DevelopmentDomain, 
  ProfileState,
  MilestoneState,
  ConcernState,
  calculateAgeInMonths,
  updateMilestoneState,
  updateConcernState,
  getStandardDomains,
  getPremiumDomains,
  migrateProfileData
} from '../types/profile';
import { appConfig, getDevelopmentDomains, getMaxChildren, isDevelopment } from '../config/configManager';
import { createUserStore, UserStore } from './userStore';
import { createContentStore } from './contentStore';
import { AppProfileStorage } from '../services/AppProfileStorage';
import { 
  AppProfileDto, 
  AppProfilePHIDto, 
  MVarDataDto,
  AdminActionDto,
  ProfileConnectionDto,
  createEmptyAppProfileDto 
} from '../types/api/appProfile';
import type { WellCheckDto, GrowthTrackDto, VaccineTrackDto, PrescriptionTrackDto, ReferralTrackDto, PrescriptionEventDto, ReferralEventDto } from '../storage/WellCheckStorage';
import { ServiceRequestDto } from '../types/api/service';

// Use a local alias so shared/web builds don't require Node global typings.
const global: any = globalThis as any;

// Milestone tracking
export interface Milestone {
  id: string;
  title: string;
  description: string;
  category: 'physical' | 'cognitive' | 'social' | 'language';
  expectedAgeMonths: number;
  achievedDate: string | null;
  notes: string;
  isCustom: boolean;
}

export interface MilestonesState {
  milestones: Milestone[];
  customMilestones: Milestone[];
  
  // Actions
  addMilestone: (milestone: Milestone) => void;
  updateMilestone: (id: string, updates: Partial<Milestone>) => void;
  achieveMilestone: (id: string, date: string, notes?: string) => void;
  removeMilestone: (id: string) => void;
  addCustomMilestone: (milestone: Omit<Milestone, 'id' | 'isCustom'>) => void;
  reset: () => void;
}

// Concerns and questions
export interface Concern {
  id: string;
  title: string;
  description: string;
  category: 'sleep' | 'feeding' | 'behavior' | 'development' | 'health' | 'other';
  priority: 'low' | 'medium' | 'high';
  status: 'open' | 'addressed' | 'resolved';
  createdDate: string;
  resolvedDate: string | null;
  notes: string;
  discussedWithProvider: boolean;
}

export interface ConcernsState {
  concerns: Concern[];
  
  // Actions
  addConcern: (concern: Omit<Concern, 'id' | 'createdDate'>) => void;
  updateConcern: (id: string, updates: Partial<Concern>) => void;
  resolveConcern: (id: string, notes?: string) => void;
  removeConcern: (id: string) => void;
  reset: () => void;
}

// Well-visit appointments
export interface WellVisit {
  id: string;
  scheduledDate: string;
  ageMonths: number;
  provider: string;
  location: string;
  status: 'scheduled' | 'completed' | 'missed' | 'cancelled';
  notes: string;
  vaccinations: string[];
  measurements: {
    weight?: number;
    height?: number;
    headCircumference?: number;
    weightPercentile?: number;
    heightPercentile?: number;
  };
  concerns: string[];
  recommendations: string[];
}

export interface WellVisitsState {
  visits: WellVisit[];
  defaultProvider: string;
  defaultLocation: string;
  
  // Actions
  addVisit: (visit: Omit<WellVisit, 'id'>) => void;
  updateVisit: (id: string, updates: Partial<WellVisit>) => void;
  completeVisit: (id: string, updates: Partial<WellVisit>) => void;
  removeVisit: (id: string) => void;
  setDefaultProvider: (provider: string) => void;
  setDefaultLocation: (location: string) => void;
  reset: () => void;
}

// Action items and tasks
export interface ActionItem {
  id: string;
  title: string;
  description: string;
  category: 'health' | 'appointment' | 'documentation' | 'milestone' | 'other';
  priority: 'low' | 'medium' | 'high';
  status: 'pending' | 'in-progress' | 'completed';
  dueDate: string | null;
  completedDate: string | null;
  notes: string;
  relatedItemId?: string; // Link to concern, visit, milestone, etc.
  relatedItemType?: 'concern' | 'visit' | 'milestone';
}

export interface ActionsState {
  actions: ActionItem[];
  
  // Actions
  addAction: (action: Omit<ActionItem, 'id'>) => void;
  updateAction: (id: string, updates: Partial<ActionItem>) => void;
  completeAction: (id: string, notes?: string) => void;
  removeAction: (id: string) => void;
  reset: () => void;
}

// Abstract storage interface that can be implemented per platform
export interface StorageInterface {
  getItem: (name: string) => Promise<string | null>;
  setItem: (name: string, value: string) => Promise<void>;
  removeItem: (name: string) => Promise<void>;
}

// Enhanced ProfileState with AppProfileDto integration
export interface EnhancedProfileState extends ProfileState {
  // AppProfile storage integration
  appProfileStorage: AppProfileStorage | null;
  
  // AppProfile management methods
  initializeAppProfileStorage: (storage: StorageInterface) => void;
  syncProfileWithAppProfile: (profileId: string, accountId: string) => Promise<void>;
  updateAppProfilePHI: (profileId: string, phi: Partial<AppProfilePHIDto>) => Promise<void>;
  updateAppProfileMilestone: (profileId: string, name: string, value: string) => Promise<void>;
  updateAppProfileConcern: (profileId: string, name: string, value: string) => Promise<void>;
  getAppProfile: (profileId: string) => Promise<AppProfileDto | null>;
  getAllAppProfiles: () => Promise<AppProfileDto[]>;
  
  // Backend sync methods
  syncProfileToBackend: (profileId: string) => Promise<boolean>;
  markProfileDirty: (profileId: string, reason?: string) => void;
  retryDirtyProfiles: () => Promise<void>;
  getDirtyProfiles: () => ChildProfile[];
  checkNetworkConnectivity: () => Promise<boolean>;
  
  // Utility methods for debugging and testing
  debugPersistence: () => Promise<any>;
  forceRehydrate: () => Promise<boolean>;
}

// Factory function to create stores with platform-specific storage
// Global flag to prevent multiple store initializations
let isStoreInitialized = false;

export const createStoresWithStorage = (storage: StorageInterface) => {
  console.log('📋 createStoresWithStorage called, isInitialized:', isStoreInitialized);
  
  // CRITICAL: Always check and fix storage corruption FIRST
  const currentGlobalStorage = (global as any).__childNavStorage;
 
  // Detect storage corruption IMMEDIATELY
  // Only check for method presence — do NOT check constructor name, because
  // the web platform uses LocalStorageAdapter while mobile uses
  // ChunkedAsyncStorageAdapter.  A name mismatch is not a corruption.
  const isStorageCorrupted = !currentGlobalStorage || 
    !currentGlobalStorage.getItem || 
    !currentGlobalStorage.setItem;
  
  if (isStorageCorrupted && storage) {
    (global as any).__childNavStorage = storage;
    
    // CRITICAL: Always force complete re-initialization when storage corruption is detected
    console.log('🔄 Forcing complete re-initialization due to storage corruption');
    isStoreInitialized = false;
    (global as any).__childNavStores = null;
  }
  
  if (isStoreInitialized) {
    console.log('📋 Stores already initialized, returning existing instance');
    // Return existing stores if already initialized
    return {
      useUserStore: (global as any).__childNavStores?.useUserStore,
      useMilestonesStore: (global as any).__childNavStores?.useMilestonesStore,
      useConcernsStore: (global as any).__childNavStores?.useConcernsStore,
      useWellVisitsStore: (global as any).__childNavStores?.useWellVisitsStore,
      useActionsStore: (global as any).__childNavStores?.useActionsStore,
      useProfileStore: (global as any).__childNavStores?.useProfileStore,
      useContentStore: (global as any).__childNavStores?.useContentStore,
      setApiService: (global as any).__childNavStores?.setApiService,
      initializeApp: (global as any).__childNavStores?.initializeApp,
    };
  }
  const zustandStorage = createJSONStorage(() => storage);

  // Create user store with storage only
  // ApiService will be set later by platform-specific initialization
  const useUserStore = createUserStore({ storage });

  const useMilestonesStore = create<MilestonesState>()(
    persist(
      (set, get) => ({
        milestones: [],
        customMilestones: [],
        
        addMilestone: (milestone) => set((state) => ({
          milestones: [...state.milestones, milestone]
        })),
        updateMilestone: (id, updates) => set((state) => ({
          milestones: state.milestones.map(m => m.id === id ? { ...m, ...updates } : m)
        })),
        achieveMilestone: (id, date, notes = '') => set((state) => ({
          milestones: state.milestones.map(m => 
            m.id === id ? { ...m, achievedDate: date, notes } : m
          )
        })),
        removeMilestone: (id) => set((state) => ({
          milestones: state.milestones.filter(m => m.id !== id)
        })),
        addCustomMilestone: (milestone) => {
          const id = `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          set((state) => ({
            customMilestones: [...state.customMilestones, { ...milestone, id, isCustom: true }]
          }));
        },
        reset: () => set({ milestones: [], customMilestones: [] }),
      }),
      {
        name: 'child-nav-milestones',
        storage: zustandStorage,
      }
    )
  );

  const useConcernsStore = create<ConcernsState>()(
    persist(
      (set, get) => ({
        concerns: [],
        
        addConcern: (concern) => {
          const id = `concern_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          set((state) => ({
            concerns: [...state.concerns, { ...concern, id, createdDate: new Date().toISOString() }]
          }));
        },
        updateConcern: (id, updates) => set((state) => ({
          concerns: state.concerns.map(c => c.id === id ? { ...c, ...updates } : c)
        })),
        resolveConcern: (id, notes = '') => set((state) => ({
          concerns: state.concerns.map(c => 
            c.id === id ? { 
              ...c, 
              status: 'resolved', 
              resolvedDate: new Date().toISOString(),
              notes: notes || c.notes 
            } : c
          )
        })),
        removeConcern: (id) => set((state) => ({
          concerns: state.concerns.filter(c => c.id !== id)
        })),
        reset: () => set({ concerns: [] }),
      }),
      {
        name: 'child-nav-concerns',
        storage: zustandStorage,
      }
    )
  );

  const useWellVisitsStore = create<WellVisitsState>()(
    persist(
      (set, get) => ({
        visits: [],
        defaultProvider: '',
        defaultLocation: '',
        
        addVisit: (visit) => {
          const id = `visit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          set((state) => ({
            visits: [...state.visits, { ...visit, id }]
          }));
        },
        updateVisit: (id, updates) => set((state) => ({
          visits: state.visits.map(v => v.id === id ? { ...v, ...updates } : v)
        })),
        completeVisit: (id, updates) => set((state) => ({
          visits: state.visits.map(v => 
            v.id === id ? { ...v, ...updates, status: 'completed' } : v
          )
        })),
        removeVisit: (id) => set((state) => ({
          visits: state.visits.filter(v => v.id !== id)
        })),
        setDefaultProvider: (provider) => set({ defaultProvider: provider }),
        setDefaultLocation: (location) => set({ defaultLocation: location }),
        reset: () => set({ visits: [], defaultProvider: '', defaultLocation: '' }),
      }),
      {
        name: 'child-nav-visits',
        storage: zustandStorage,
      }
    )
  );

  const useActionsStore = create<ActionsState>()(
    persist(
      (set, get) => ({
        actions: [],
        
        addAction: (action) => {
          const id = `action_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          set((state) => ({
            actions: [...state.actions, { ...action, id }]
          }));
        },
        updateAction: (id, updates) => set((state) => ({
          actions: state.actions.map(a => a.id === id ? { ...a, ...updates } : a)
        })),
        completeAction: (id, notes = '') => set((state) => ({
          actions: state.actions.map(a => 
            a.id === id ? { 
              ...a, 
              status: 'completed', 
              completedDate: new Date().toISOString(),
              notes: notes || a.notes 
            } : a
          )
        })),
        removeAction: (id) => set((state) => ({
          actions: state.actions.filter(a => a.id !== id)
        })),
        reset: () => set({ actions: [] }),
      }),
      {
        name: 'child-nav-actions',
        storage: zustandStorage,
      }
    )
  );

  const useContentStore = createContentStore(storage);

  // Helper function to convert ChildProfile to AppProfileDto for backend sync
  const convertChildProfileToAppProfileDto = (profile: ChildProfile, accountId: string): AppProfileDto => {
    const fallbackNameParts = (profile.name || '').trim().split(/\s+/).filter(Boolean);
    const phiFname = (profile.fname || '').trim() || fallbackNameParts[0] || '';
    const phiLname = (profile.lname || '').trim() || fallbackNameParts.slice(1).join(' ');
    const phiDisplayName = (profile.name || '').trim() || [phiFname, phiLname].filter(Boolean).join(' ').trim();

    // Emit strict backend DTO fields only. Do not include local UI aliases.
    const toPrescriptionEvent = (event: PrescriptionEventDto) => ({
      Id: event.id || '',
      Name: event.name || '',
      Date: event.date || '',
      Freq: event.freq || '',
      Qty: event.qty ?? event.totalDoses ?? '',
      Refills: event.refills || '',
      Track: event.Track ?? event.track ?? (event.trackAction === false ? '' : 'Track'),
      Status: event.status || 'Active',
    });

    // Emit strict backend DTO fields only. Do not include local UI aliases.
    const toReferralEvent = (event: ReferralEventDto) => ({
      Id: event.id || '',
      To: event.to || '',
      For: event.for || '',
      Date: event.date || '',
      ContactInfo: event.contactInfo || '',
      Outcome: event.outcome || '',
      Track: event.Track ?? event.track ?? (event.trackAction === false ? '' : 'Track'),
      Status: event.status || 'Pending',
    });

    const toWellCheckEvent = (event: WellCheckDto): WellCheckDto => ({
      id: event.id || '',
      eventId: event.eventId || '',
      date: event.date || '',
      type: event.type || '',
      location: event.location || '',
      outcome: event.outcome || '',
      status: event.status || '',
    });

    const prescriptionEvents = ((profile as any).prescriptiontrack?.events ?? (profile as any).prescriptiontrack?.Events ?? []).map(toPrescriptionEvent);
    const referralEvents = ((profile as any).referraltrack?.events ?? (profile as any).referraltrack?.Events ?? []).map(toReferralEvent);
    const cdcPremature = profile.cdc_premature === 1 ? 1 : 0;
    const cdcPremGest = typeof profile.cdc_premgest === 'number'
      ? Math.min(40, Math.max(21, Math.round(profile.cdc_premgest)))
      : null;
    const cdcPremWeek = cdcPremature === 1 && cdcPremGest !== null
      ? Math.max(0, 40 - cdcPremGest)
      : null;

    // Convert milestone states to MVarData format
    const milestones: MVarDataDto[] = profile.milestonestate.map(ms => ({
      name: ms.mvarid, // Use mvarid string directly (e.g., "mvar001", "mvar002")
      value: ms.state === 'yes' ? '1' : ms.state === 'sometimes' ? '2' : '3'
    }));

    const toDtoString = (value: unknown): string => {
      if (value === undefined || value === null) return '';
      return String(value);
    };

    // AdminActionDto on backend is string-only. Normalize all outbound fields to strings.
    const actions = (profile.actionsstate ?? []).map((action: any) => ({
      source: toDtoString(action.source ?? action.Source),
      group: toDtoString(action.group ?? action.Group),
      mvar: toDtoString(action.mvar ?? action.MVar),
      rank: toDtoString(action.rank ?? action.Rank),
      action: toDtoString(action.action ?? action.Action),
      dueDate: toDtoString(action.dueDate ?? action.DueDate),
      location: toDtoString(action.location ?? action.Location),
      schedule: toDtoString(action.schedule ?? action.Schedule),
      actual: toDtoString(action.actual ?? action.Actual),
      expense: toDtoString(action.expense ?? action.Expense),
      importance: toDtoString(action.importance ?? action.Importance),
      outcome: toDtoString(action.outcome ?? action.Outcome),
      created: toDtoString(action.created ?? action.Created),
      completed: toDtoString(action.completed ?? action.Completed),
      status: toDtoString(action.status ?? action.Status),
    }));

    // Convert devFloor, devCeiling, devState, devAge, devDelay, and devResultScore to domainstate data
    const domainstate: MVarDataDto[] = [];
    
    // Add devFloor data
    Object.entries(profile.devfloor || {}).forEach(([domain, value]) => {
      domainstate.push({
        name: `devFloor_${domain}`, // Use descriptive string name
        value: typeof value === 'number' ? value.toString() : '0'
      });
    });

    // Add devCeiling data
    Object.entries(profile.devceiling || {}).forEach(([domain, value]) => {
      domainstate.push({
        name: `devCeiling_${domain}`, // Use descriptive string name
        value: typeof value === 'number' ? value.toString() : '0'
      });
    });

    // Add devstate data
    Object.entries(profile.devstate || {}).forEach(([domain, value]) => {
      domainstate.push({
        name: `devState_${domain}`,
        value: typeof value === 'number' ? Math.round(value).toString() : '0'
      });
    });

    // Add devage data
    Object.entries(profile.devage || {}).forEach(([domain, value]) => {
      domainstate.push({
        name: `devAge_${domain}`, // Use descriptive string name
        value: typeof value === 'number' ? (Math.round(value * 10) / 10).toString() : '0' // Store as decimal string
      });
    });

    // Add devdelay data
    Object.entries(profile.devdelay || {}).forEach(([domain, value]) => {
      domainstate.push({
        name: `devDelay_${domain}`, // Use descriptive string name
        value: typeof value === 'number' ? (Math.round(value * 10) / 10).toString() : '0' // Store as decimal string
      });
    });

    // Add devresultscore data
    Object.entries(profile.devresultscore || {}).forEach(([domain, value]) => {
      domainstate.push({
        name: `devResultScore_${domain}`, // Use descriptive string name
        value: typeof value === 'number' ? Math.round(value).toString() : '0'
      });
    });

    // Add milestone domain timestamps
    Object.entries(profile.devfloorCeilingTimestamp || {}).forEach(([domain, value]) => {
      domainstate.push({
        name: `devTimestamp_${domain}`, // Use descriptive string name for timestamps
        value: value || new Date().toISOString() // Store as ISO string, default to current time if missing
      });
    });

    // Add milestone domain-review computed timestamps (devresultcomputed per domain)
    Object.entries(profile.devresultcomputed || {}).forEach(([domain, value]) => {
      if (value) { // only persist when a timestamp has actually been set
        domainstate.push({
          name: `devResultComputed_${domain}`,
          value: value as string
        });
      }
    });

    return {
      profileId: profile.id,
      accountId,
      phi: {
        dname: phiDisplayName,
        fname: phiFname,
        lname: phiLname,
        email: profile.email || '',
        dob: profile.dob || '',
        gender: profile.gender || '',
        extra: [
          { name: 'agephysical', value: profile.agephysical ? (Math.round(profile.agephysical * 10) / 10).toString() : '0' },
          { name: 'isPremium', value: profile.isPremium ? '1' : '0' },
          { name: 'cdc_premature', value: cdcPremature.toString() },
          ...(cdcPremature === 1 && cdcPremGest !== null
            ? [
                { name: 'cdc_premgest', value: cdcPremGest.toString() },
                { name: 'cdc_premweek', value: (cdcPremWeek ?? 0).toString() },
              ]
            : []),
        ]
      },
      milestones,
      concerns: (() => {
        const result: MVarDataDto[] = [];
        const seenNames = new Set<string>(); // deduplicate by mvarName
        for (const cs of (profile.concernsstate ?? [])) {
          // Domain-review sentinel entries: stored with the review timestamp as value
          if (cs.mvarid.startsWith('domain_viewed_')) {
            if (cs.lastUpdated) {
              result.push({ name: cs.mvarid, value: cs.lastUpdated });
            }
            continue;
          }

          let mvarName: string;
          if (cs.mvarid.startsWith('devage_')) {
            // Legacy migration: devage_{domain} → cn_{code}_measure
            const domainKey = cs.mvarid.slice('devage_'.length);
            const DEVAGE_TO_MVAR: Record<string, string> = {
              communication:   'cn_lc_measure',
              finemotor:       'cn_fm_measure',
              grossmotor:      'cn_gm_measure',
              cognitive:       'cn_cg_measure',
              socialemotional: 'cn_se_measure',
              motor:           'cn_mt_measure',
            };
            mvarName = DEVAGE_TO_MVAR[domainKey] ?? `${domainKey}_concern`;
          } else {
            // Regular concern: mvarid is already the mvarname (e.g. cn_behavior_1)
            mvarName = cs.mvarid;
          }

          // Skip duplicates (last one in list would win if not deduplicated)
          if (seenNames.has(mvarName)) continue;
          seenNames.add(mvarName);

          // State entry: '1' = identified (yes), '0' = not identified (no)
          result.push({ name: mvarName, value: cs.state === 'yes' ? '1' : '0' });

          // Action entry (only when a valid action 1-3 is assigned)
          if (cs.action !== undefined && cs.action !== null && cs.action > 0) {
            result.push({ name: `${mvarName}_action`, value: cs.action.toString() });
          }
        }
        return result;
      })(),
      goals: [], // Could add developmental goals if needed
      actions: actions as AdminActionDto[],
      adminstates: [], // AdminStateDto objects loaded separately by profileId match
      domainstate,
      demogs: [],
      medical: [],
      wellchecks: (profile.wellchecks ?? []).map(toWellCheckEvent),
      growth: (profile.growthtrack ?? { data: [] }) as GrowthTrackDto,
      vaccines: (profile.vaccinetrack ?? { events: [] }) as VaccineTrackDto,
      prescriptions: { events: prescriptionEvents } as PrescriptionTrackDto,
      referrals: { events: referralEvents } as ReferralTrackDto,
      modified: new Date().toISOString(),
      status: profile.profileStatus !== undefined
        ? profile.profileStatus.toString()
        : profile.isHidden ? '0' : '1',
    };
  };

  // Create the enhanced profile store
  const useProfileStore = create<EnhancedProfileState>()(
    persist(
      (set, get) => ({
        profiles: [],
        activeProfileId: null,
        developmentAges: {},
        
        // AppProfile storage integration
        appProfileStorage: null,
        
        addProfile: async (profile, options) => {
          
          const state = get();
          const maxChildren = getMaxChildren();
          
          // Note: Profile limit removed - unlimited profiles allowed for all tiers
          if (state.profiles.length >= maxChildren && maxChildren !== Infinity) {
            console.warn(`Profile limit reached. Maximum ${maxChildren} profiles allowed for ${appConfig.getConfig().tier} tier.`);
            return;
          }
          
          // Use existing profile ID if provided (from server sync), otherwise generate new ID
          const id = (profile as any).id || `profile_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          const now = new Date().toISOString();
          
          // Calculate physical age if not provided
          const agephysical = profile.agephysical ?? (profile.dob ? calculateAgeInMonths(profile.dob) : 0);
          
          // ✅ CRITICAL FIX: Determine if profile is premium EARLY
          const profileIsPremium = profile.isPremium ?? appConfig.isPremium();


          const newProfile: ChildProfile = {
            // Core profile information
            name: profile.name,
            fname: profile.fname,
            lname: profile.lname,
            email: profile.email,
            accountnum: profile.accountnum,
            dob: profile.dob,
            gender: profile.gender,
            agephysical,
            
            // Development age tracking - use provided values or defaults
            devage: {
              communication: profile.devage?.communication ?? agephysical,
              motor: profile.devage?.motor ?? agephysical,
              finemotor: profile.devage?.finemotor ?? agephysical,
              grossmotor: profile.devage?.grossmotor ?? agephysical,
              cognitive: profile.devage?.cognitive ?? agephysical,
              socialemotional: profile.devage?.socialemotional ?? agephysical,
            },
            
            // Floor age tracking - use provided values or defaults to 0
            devfloor: profile.devfloor || {
              communication: 0,
              motor: 0,
              finemotor: 0,
              grossmotor: 0,
              cognitive: 0,
              socialemotional: 0,
            },
            
            // Ceiling age tracking - use provided values or current dev age
            devceiling: profile.devceiling || {
              communication: agephysical,
              motor: agephysical,
              finemotor: agephysical,
              grossmotor: agephysical,
              cognitive: agephysical,
              socialemotional: agephysical,
            },
            
            // Floor/ceiling calculation timestamps
            devfloorCeilingTimestamp: profile.devfloorCeilingTimestamp || {
              communication: now,
              motor: now,
              finemotor: now,
              grossmotor: now,
              cognitive: now,
              socialemotional: now,
            },
            
            // Delay tracking - use provided values or defaults to 0
            devdelay: profile.devdelay || {
              communication: 0,
              motor: 0,
              finemotor: 0,
              grossmotor: 0,
              cognitive: 0,
              socialemotional: 0,
            },
            
            // Result tracking - use provided values or defaults to 0
            devresult: profile.devresult || {
              communication: 0,
              motor: 0,
              finemotor: 0,
              grossmotor: 0,
              cognitive: 0,
              socialemotional: 0,
            },
            
            // Result score tracking - use provided values or defaults to 0
            devresultscore: profile.devresultscore || {
              communication: 0,
              motor: 0,
              finemotor: 0,
              grossmotor: 0,
              cognitive: 0,
              socialemotional: 0,
            },
            
            // Result computation timestamps - use provided values or defaults to null
            devresultcomputed: profile.devresultcomputed || {
              communication: null,
              motor: null,
              finemotor: null,
              grossmotor: null,
              cognitive: null,
              socialemotional: null,
            },
            
            // Development state tracking - use provided values or defaults to 0
            devstate: profile.devstate || {
              communication: 0,
              motor: 0,
              finemotor: 0,
              grossmotor: 0,
              cognitive: 0,
              socialemotional: 0,
            },
            
            // State tracking arrays - use provided values or empty arrays
            milestonestate: profile.milestonestate || [],
            concernsstate: profile.concernsstate || [],
            actionsstate: (profile as any).actionsstate || [],
            
            // Metadata
            id,
            photo: profile.photo,
            createdDate: (profile as any).createdDate || now,
            lastActiveDate: (profile as any).lastActiveDate || now,
            lastSyncDate: profile.lastSyncDate,
            
            // App configuration
            //isPremium: profile.isPremium ?? appConfig.isPremium(),
            isPremium: profileIsPremium,
            
            // Visibility / status — preserve values from incoming profile (e.g. server-loaded disabled profile)
            isHidden: (profile as any).isHidden ?? false,
            profileStatus: (profile as any).profileStatus ?? 1,
            
            // Profile connections — carry over from incoming data (populated from account_get_info or profile_get)
            connections: profile.connections ?? [],

            // Medical track data — carry over from incoming profile (populated by convertAppProfileToChildProfile)
            wellchecks: (profile as any).wellchecks,
            growthtrack: (profile as any).growthtrack,
            vaccinetrack: (profile as any).vaccinetrack,
            prescriptiontrack: (profile as any).prescriptiontrack,
            referraltrack: (profile as any).referraltrack,
            
            // Initialize sync state
            isDirty: false,
            lastSyncAttempt: undefined,
            syncFailureReason: undefined,
          };
          
          
          set((state) => ({
            profiles: [...state.profiles, newProfile],
            activeProfileId: id, // Always set newly added profile as active
          }));
          
          // Initialize development age tracking
          get().calculateDevelopmentAge(id);
          
          // First sync with AppProfile (local storage)
          const userState = useUserStore.getState();
          const familyId = userState.session?.familyAccount?.familyId;
          const accountId = familyId || 'default-account';
          
          if (get().appProfileStorage) {
            get().syncProfileWithAppProfile(id, accountId).catch(error => {
              console.error('Failed to sync new profile with AppProfile:', error);
            });
          } 
          
          // Automatic backend sync when account is active (signed in)
          
          // First attempt to refresh authentication if it seems stale
          if (userState.session.isAuthenticated !== true && userState.session.user?.authToken) {
            try {
              const refreshed = await userState.refreshAuth();

            } catch (error) {
              console.warn('📋 addProfile: Authentication refresh failed:', error);
            }
          }
          
          // More robust authentication check - consider authenticated if:
          // 1. isAuthenticated is explicitly true, OR
          // 2. We have a user with an auth token and valid session
          const isAuthenticated = userState.session.isAuthenticated === true || 
            (userState.session.user && userState.session.user.authToken && userState.isSessionValid());
          
          // Also check if user has previously set up an account
          const checkPreviousLogin = async () => {
            try {
              return (await userState.checkStoredAuth()) || 
                     (await userState.getStoredItem('user_email')) ||
                     (await userState.getStoredItem('auth_timestamp'));
            } catch (error) {
              console.warn('📋 addProfile: Error checking previous login:', error);
              return false;
            }
          };
          
          const userHasPreviouslyLoggedIn = await checkPreviousLogin();
            
          if (options?.skipBackendSync) {
            // Caller explicitly requested no backend sync (e.g. profile was just downloaded from server)
          } else if (isAuthenticated && userHasPreviouslyLoggedIn) {
            
            // Perform backend sync asynchronously (don't block UI)
            get().syncProfileToBackend(id).then(success => {
              if (success) {
                console.log('✅ addProfile: Automatic backend sync successful for profile:', newProfile.name);
              } else {
                console.log('❌ addProfile: Automatic backend sync failed for profile:', newProfile.name);
                console.log('   Profile marked as dirty and will be retried when session becomes available');
              }
            }).catch(error => {
              console.error('❌ addProfile: Automatic backend sync error:', error);
              console.log('   Profile marked as dirty and will be retried when session becomes available');
            });
          } 
        },
        
        updateProfile: (id, updates) => {
          set((state) => ({
            profiles: state.profiles.map(p => 
              p.id === id ? { ...p, ...updates, lastActiveDate: new Date().toISOString() } : p
            )
          }));

          // Only mark as modified if this isn't a sync-related update
          const isSyncUpdate = updates.hasOwnProperty('isDirty') || 
                              updates.hasOwnProperty('lastSyncDate') || 
                              updates.hasOwnProperty('lastSyncAttempt') || 
                              updates.hasOwnProperty('syncFailureReason');

          // Connections are account-level metadata and do not belong in AppProfileDto.
          // Avoid writing full AppProfile records on connection-only updates.
          const isConnectionOnlyUpdate = Object.keys(updates).length > 0 && Object.keys(updates).every(k => k === 'connections');

          // Computed developmental fields (floor, ceiling, age, delay) are intermediate calculation
          // artifacts recalculated frequently — skip AppProfileStorage writes for these alone.
          // IMPORTANT: devresult, devresultscore, devresultcomputed are the final OUTPUT values that
          // must be persisted to AppProfileStorage so they survive cold restarts via AppProfile sync.
          const COMPUTED_KEYS = new Set(['devfloor','devceiling','devstate','devage','devdelay','devfloorCeilingTimestamp','agephysical','lastActiveDate']);
          const isComputedOnlyUpdate = Object.keys(updates).length > 0 && Object.keys(updates).every(k => COMPUTED_KEYS.has(k));


          const state = get();
          const updatedProfile = state.getProfileById(id);
          if (updatedProfile && state.appProfileStorage && !isSyncUpdate && !isConnectionOnlyUpdate && !isComputedOnlyUpdate) {
            const userState = useUserStore.getState();
            const accountId = updatedProfile.accountnum || userState.session?.familyAccount?.familyId || 'default-account';
            const appProfileDto = convertChildProfileToAppProfileDto(updatedProfile, accountId);

            state.appProfileStorage.getProfile(id)
              .then(existingProfile => {

                // Guard against partial in-memory profile state during sign-in/rehydration.
                // If tracks are missing locally for this update, preserve existing persisted tracks.
                if (existingProfile) {
                  if (updatedProfile.prescriptiontrack === undefined && existingProfile.prescriptions) {
                    appProfileDto.prescriptions = existingProfile.prescriptions;
                  }
                  if (updatedProfile.referraltrack === undefined && existingProfile.referrals) {
                    appProfileDto.referrals = existingProfile.referrals;
                  }
                }

                return state.appProfileStorage!.saveProfile(appProfileDto);
              })
              .catch(error => {
                console.error('📋 updateProfile: Failed to persist AppProfileDto:', error);
              });
          }
          
        },
        
        removeProfile: (id) => set((state) => {
          const newProfiles = state.profiles.filter(p => p.id !== id);
          const newDevelopmentAges = { ...state.developmentAges };
          delete newDevelopmentAges[id];
          
          return {
            profiles: newProfiles,
            activeProfileId: state.activeProfileId === id 
              ? (newProfiles.length > 0 ? newProfiles[0].id : null)
              : state.activeProfileId,
            developmentAges: newDevelopmentAges,
          };
        }),
        
        setActiveProfile: (id) => {
          set((state) => ({
            activeProfileId: id,
            profiles: state.profiles.map(p => 
              p.id === id ? { ...p, lastActiveDate: new Date().toISOString() } : p
            )
          }));
        },
        
        updateDevelopmentAge: (profileId, domains) => set((state) => ({
          developmentAges: {
            ...state.developmentAges,
            [profileId]: {
              profileId,
              domains,
              overallAgeMonths: Math.round(domains.reduce((sum, d) => sum + d.ageMonths, 0) / domains.length),
              lastCalculated: new Date().toISOString(),
            },
          },
        })),
        
        calculateDevelopmentAge: (profileId) => {
          const profile = get().getProfileById(profileId);
          if (!profile) return;
          
          const currentAge = calculateAgeInMonths(profile.dob);
          
          // Use configuration to determine domain structure
          const domainKeys = getDevelopmentDomains();
          const domains: DevelopmentDomain[] = domainKeys.map(key => {
            // Map domain keys to proper names
            const domainNames: Record<string, string> = {
              communication: 'Communication',
              motor: 'Motor Skills',
              fineMotor: 'Fine Motor',
              grossMotor: 'Gross Motor',
              cognitive: 'Cognitive',
              socialEmotional: 'Social-Emotional',
            };
            
            return {
              name: domainNames[key] || key,
              key: key as any,
              ageMonths: currentAge,
              lastUpdated: new Date().toISOString(),
              ...(appConfig.hasFeature('percentileTracking') && { percentile: 50 }), // Default 50th percentile
            };
          });
          
          // Initialize all domains to current chronological age
          // In a real app, this would be calculated from completed milestones
          const initializedDomains = domains.map(domain => ({
            ...domain,
            ageMonths: currentAge,
            lastUpdated: new Date().toISOString(),
          }));
          
          get().updateDevelopmentAge(profileId, initializedDomains);
        },
        
        getActiveProfile: () => {
          const state = get();
          const activeProfile = state.profiles.find(p => p.id === state.activeProfileId) || null;
          return activeProfile;
        },
        
        getActiveDevelopmentAge: () => {
          const state = get();
          const activeProfile = state.getActiveProfile();
          return activeProfile ? state.developmentAges[activeProfile.id] || null : null;
        },
        
        getProfileById: (id) => {
          return get().profiles.find(p => p.id === id) || null;
        },

        // Milestone state management
        updateMilestoneState: (profileId, mvarid, state, notes) => {
          console.log('📋 updateMilestoneState: User input for profile', profileId, 'mvarid:', mvarid, 'state:', state);
          set((prevState) => ({
            profiles: prevState.profiles.map(p => 
              p.id === profileId 
                ? { ...p, milestonestate: updateMilestoneState(p.milestonestate, mvarid, state, notes) }
                : p
            )
          }));
          
          // Mark profile as dirty — user has entered new milestone data that needs syncing
          get().markProfileDirty(profileId, `Milestone answer updated by user: ${mvarid}=${state}`);
          console.log('📋 updateMilestoneState: Profile marked dirty for', profileId);
          
          // Sync with AppProfile (async, don't block UI)
          // Convert state to string value: 'yes'='1', 'sometimes'='2', 'not-yet'='0'
          const stateValue = state === 'yes' ? '1' : state === 'sometimes' ? '2' : '0';
          // Use mvarid string directly (e.g., "mvar001")
          get().updateAppProfileMilestone(profileId, mvarid, stateValue).catch(error => {
            console.error('Failed to sync milestone with AppProfile:', error);
          });
        },

        getMilestoneState: (profileId, mvarid) => {
          const profile = get().getProfileById(profileId);
          return profile?.milestonestate.find(s => s.mvarid === mvarid) || null;
        },

        getMilestoneStates: (profileId) => {
          const profile = get().getProfileById(profileId);
          return profile?.milestonestate || [];
        },

        // Concern state management
        updateConcernState: (profileId, mvarid, state, notes = '') => {
          console.log('📋 updateConcernState:', profileId, mvarid, state);
          set((prevState) => ({
            profiles: prevState.profiles.map(p => 
              p.id === profileId 
                ? { 
                    ...p, 
                    concernsstate: updateConcernState(p.concernsstate, mvarid, state, notes),
                    lastActiveDate: new Date().toISOString()
                  }
                : p
            )
          }));
          
          // Mark profile as dirty — user has entered new concern data that needs syncing
          get().markProfileDirty(profileId, `Concern answer updated by user: ${mvarid}=${state}`);
          console.log('📋 updateConcernState: Profile marked dirty for', profileId, mvarid, state);
          
          // Sync with AppProfile (local storage)
          const userState = useUserStore.getState();
          const familyId = userState.session?.familyAccount?.familyId;
          const accountId = familyId || 'default-account';
          
          // Convert state to string value: 'yes'='1', 'no'='0'
          const stateValue = state === 'yes' ? '1' : '0';
          // Use mvarid string directly (e.g., "mvar001")
          get().updateAppProfileConcern(profileId, mvarid, stateValue).catch(error => {
            console.error('Failed to sync concern with AppProfile:', error);
          });
          
          console.log('📋 updateConcernState: Concern updated, persistence triggered');
        },

        getConcernState: (profileId, mvarid) => {
          const profile = get().getProfileById(profileId);
          return profile?.concernsstate.find(s => s.mvarid === mvarid) || null;
        },

        getConcernStates: (profileId) => {
          const profile = get().getProfileById(profileId);
          return profile?.concernsstate || [];
        },

        // Age management
        updateDevelopmentAges: (profileId, ages) => {
          set((prevState) => ({
            profiles: prevState.profiles.map(p => 
              p.id === profileId 
                ? { ...p, ...ages }
                : p
            )
          }));
          console.log('📋 updateDevelopmentAges: Updated development ages for', profileId);
        },

        calculatePhysicalAge: (profileId) => {
          const profile = get().getProfileById(profileId);
          return profile ? calculateAgeInMonths(profile.dob) : 0;
        },

        // Statistics and insights
        getProfileStats: (profileId) => {
          const profile = get().getProfileById(profileId);
          if (!profile) {
            return {
              totalMilestones: 0,
              achievedMilestones: 0,
              sometimesMilestones: 0,
              notYetMilestones: 0,
              totalConcerns: 0,
              identifiedConcerns: 0,
              developmentAgeGaps: {
                communication: 0,
                motor: 0,
                finemotor: 0,
                grossmotor: 0,
                cognitive: 0,
                socialemotional: 0,
              },
              floorAgeGaps: {
                communication: 0,
                motor: 0,
                finemotor: 0,
                grossmotor: 0,
                cognitive: 0,
                socialemotional: 0,
              },
              ceilingAgeGaps: {
                communication: 0,
                motor: 0,
                finemotor: 0,
                grossmotor: 0,
                cognitive: 0,
                socialemotional: 0,
              },
              delayTracking: {
                communication: 0,
                motor: 0,
                finemotor: 0,
                grossmotor: 0,
                cognitive: 0,
                socialemotional: 0,
              },
              assessmentResults: {
                communication: 0,
                motor: 0,
                finemotor: 0,
                grossmotor: 0,
                cognitive: 0,
                socialemotional: 0,
              },
              assessmentResultScores: {
                communication: 0,
                motor: 0,
                finemotor: 0,
                grossmotor: 0,
                cognitive: 0,
                socialemotional: 0,
              },
            };
          }

          const physicalAge = profile.agephysical;
          const achievedMilestones = profile.milestonestate.filter(s => s.state === 'yes').length;
          const sometimesMilestones = profile.milestonestate.filter(s => s.state === 'sometimes').length;
          const notYetMilestones = profile.milestonestate.filter(s => s.state === 'not-yet').length;
          const identifiedConcerns = profile.concernsstate.filter(s => s.state === 'yes').length;

          return {
            totalMilestones: profile.milestonestate.length,
            achievedMilestones,
            sometimesMilestones,
            notYetMilestones,
            totalConcerns: profile.concernsstate.length,
            identifiedConcerns,
            developmentAgeGaps: {
              communication: profile.devage.communication ? profile.devage.communication - physicalAge : 0,
              motor: profile.devage.motor ? profile.devage.motor - physicalAge : 0,
              finemotor: profile.devage.finemotor ? profile.devage.finemotor - physicalAge : 0,
              grossmotor: profile.devage.grossmotor ? profile.devage.grossmotor - physicalAge : 0,
              cognitive: profile.devage.cognitive ? profile.devage.cognitive - physicalAge : 0,
              socialemotional: profile.devage.socialemotional ? profile.devage.socialemotional - physicalAge : 0,
            },
            floorAgeGaps: {
              communication: profile.devfloor.communication ? profile.devfloor.communication - physicalAge : 0,
              motor: profile.devfloor.motor ? profile.devfloor.motor - physicalAge : 0,
              finemotor: profile.devfloor.finemotor ? profile.devfloor.finemotor - physicalAge : 0,
              grossmotor: profile.devfloor.grossmotor ? profile.devfloor.grossmotor - physicalAge : 0,
              cognitive: profile.devfloor.cognitive ? profile.devfloor.cognitive - physicalAge : 0,
              socialemotional: profile.devfloor.socialemotional ? profile.devfloor.socialemotional - physicalAge : 0,
            },
            ceilingAgeGaps: {
              communication: profile.devceiling.communication ? profile.devceiling.communication - physicalAge : 0,
              motor: profile.devceiling.motor ? profile.devceiling.motor - physicalAge : 0,
              finemotor: profile.devceiling.finemotor ? profile.devceiling.finemotor - physicalAge : 0,
              grossmotor: profile.devceiling.grossmotor ? profile.devceiling.grossmotor - physicalAge : 0,
              cognitive: profile.devceiling.cognitive ? profile.devceiling.cognitive - physicalAge : 0,
              socialemotional: profile.devceiling.socialemotional ? profile.devceiling.socialemotional - physicalAge : 0,
            },
            delayTracking: {
              communication: profile.devdelay.communication || 0,
              motor: profile.devdelay.motor || 0,
              finemotor: profile.devdelay.finemotor || 0,
              grossmotor: profile.devdelay.grossmotor || 0,
              cognitive: profile.devdelay.cognitive || 0,
              socialemotional: profile.devdelay.socialemotional || 0,
            },
            assessmentResults: {
              communication: profile.devresult.communication || 0,
              motor: profile.devresult.motor || 0,
              finemotor: profile.devresult.finemotor || 0,
              grossmotor: profile.devresult.grossmotor || 0,
              cognitive: profile.devresult.cognitive || 0,
              socialemotional: profile.devresult.socialemotional || 0,
            },
            assessmentResultScores: {
              communication: profile.devresultscore.communication || 0,
              motor: profile.devresultscore.motor || 0,
              finemotor: profile.devresultscore.finemotor || 0,
              grossmotor: profile.devresultscore.grossmotor || 0,
              cognitive: profile.devresultscore.cognitive || 0,
              socialemotional: profile.devresultscore.socialemotional || 0,
            },
          };
        },
        
        // AppProfile management methods
        initializeAppProfileStorage: (storage: StorageInterface) => {
          const state = get();
          
          // Don't create a new AppProfileStorage if one already exists
          if (state.appProfileStorage) {
            console.log('📋 Init: AppProfileStorage already exists, skipping creation');
            return;
          }
          
          console.log('📋 Init: Creating AppProfileStorage with storage:', !!storage);
          const appProfileStorage = new AppProfileStorage(storage);
          console.log('📋 Init: AppProfileStorage created:', !!appProfileStorage);
          console.log('📋 Init: AppProfileStorage constructor:', typeof appProfileStorage.constructor);
          console.log('📋 Init: AppProfileStorage prototype:', !!Object.getPrototypeOf(appProfileStorage));
          console.log('📋 Init: getProfile method exists:', typeof appProfileStorage.getProfile);
          console.log('📋 Init: getProfile method callable:', typeof appProfileStorage.getProfile === 'function');
          
          // Test the method directly and sync existing profiles to store
          if (typeof appProfileStorage.getProfile === 'function') {
            console.log('📋 Init: getProfile method is callable');
            
            // Load existing profiles from AppProfileStorage into the store
            appProfileStorage.getAllProfiles().then(existingProfiles => {
              console.log('📋 Init: AppProfileStorage test successful, found', existingProfiles.length, 'profiles');
              
              // Sync existing AppProfiles to the store if they're missing
              if (existingProfiles.length > 0) {
                const currentStoreState = get();
                const storeProfiles = currentStoreState.profiles;
                
                console.log('📋 Init: Current store has', storeProfiles.length, 'profiles');
                
                // Find AppProfiles that don't exist in the store
                for (const appProfile of existingProfiles) {
                  const existsInStore = storeProfiles.some(p => p.id === appProfile.profileId);
                  if (!existsInStore) {
                    console.log('📋 Init: Converting AppProfile to ChildProfile:', appProfile.profileId);
                    
                    // Convert AppProfile to ChildProfile format
                    const childProfile: ChildProfile = {
                      id: appProfile.profileId,
                      name: (appProfile.phi?.dname || '').trim() ||
                        `${appProfile.phi?.fname || ''} ${appProfile.phi?.lname || ''}`.trim() ||
                        'Unknown',
                      fname: appProfile.phi?.fname || undefined,
                      lname: appProfile.phi?.lname || undefined,
                      email: appProfile.phi?.email || undefined,
                      accountnum: appProfile.accountId,
                      dob: appProfile.phi?.dob || new Date().toISOString(),
                      gender: (appProfile.phi?.gender as 'male' | 'female' | 'other' | 'prefer-not-to-say') || 'prefer-not-to-say',
                      agephysical: 0, // Will be calculated from dob
                      devage: {
                        communication: null,
                        motor: null,
                        finemotor: null,
                        grossmotor: null,
                        cognitive: null,
                        socialemotional: null,
                      },
                      devfloor: {
                        communication: null,
                        motor: null,
                        finemotor: null,
                        grossmotor: null,
                        cognitive: null,
                        socialemotional: null,
                      },
                      devceiling: {
                        communication: null,
                        motor: null,
                        finemotor: null,
                        grossmotor: null,
                        cognitive: null,
                        socialemotional: null,
                      },
                      devfloorCeilingTimestamp: {
                        communication: new Date().toISOString(),
                        motor: new Date().toISOString(),
                        finemotor: new Date().toISOString(),
                        grossmotor: new Date().toISOString(),
                        cognitive: new Date().toISOString(),
                        socialemotional: new Date().toISOString(),
                      },
                      devdelay: {
                        communication: null,
                        motor: null,
                        finemotor: null,
                        grossmotor: null,
                        cognitive: null,
                        socialemotional: null,
                      },
                      devresult: {
                        communication: null,
                        motor: null,
                        finemotor: null,
                        grossmotor: null,
                        cognitive: null,
                        socialemotional: null,
                      },
                      devresultscore: {
                        communication: null,
                        motor: null,
                        finemotor: null,
                        grossmotor: null,
                        cognitive: null,
                        socialemotional: null,
                      },
                      devstate: {
                        communication: 0,
                        motor: 0,
                        finemotor: 0,
                        grossmotor: 0,
                        cognitive: 0,
                        socialemotional: 0,
                      },
                      milestonestate: [],
                      concernsstate: [],
                      isPremium: false,
                      createdDate: new Date().toISOString(),
                      lastActiveDate: new Date().toISOString(),
                      isDirty: false,
                      // Connections come from account-level storage; will be populated
                      // by applyAccountConnectionsToProfileStore after profile is added.
                      connections: [],
                    };
                    
                    // Add the profile to the store silently (without triggering sync)
                    currentStoreState.addProfile(childProfile, { skipBackendSync: true });
                    console.log('📋 Init: Added missing profile to store:', childProfile.id);
                  }
                }
                
                console.log('📋 Init: Profile sync completed, store now has', get().profiles.length, 'profiles');
              }
            }).catch(error => {
              console.warn('📋 Init: AppProfileStorage test failed:', error);
            });
          } else {
            console.error('📋 Init: getProfile method is not a function:', appProfileStorage.getProfile);
            console.log('📋 Init: Available methods:', Object.getOwnPropertyNames(appProfileStorage));
            console.log('📋 Init: Prototype methods:', Object.getOwnPropertyNames(Object.getPrototypeOf(appProfileStorage)));
          }
          
          set({ appProfileStorage });
          console.log('📋 Profile Store: AppProfileStorage initialized and set');
          
          // Verify it was set correctly
          const currentState = get();
          console.log('📋 Init: Storage set in state:', !!currentState.appProfileStorage);
          console.log('📋 Init: Storage getProfile method after set:', typeof currentState.appProfileStorage?.getProfile);
        },



        syncProfileWithAppProfile: async (profileId: string, accountId: string) => {
          const state = get();
          
          // Check if appProfileStorage needs to be reinitialized (after store rehydration)
          if (!state.appProfileStorage || typeof state.appProfileStorage.getProfile !== 'function') { 
            const storage = (global as any).__childNavStorage;
            if (storage) {
              state.initializeAppProfileStorage(storage);
            } else {
             return;
            }
          }
          
          if (!state.appProfileStorage || typeof state.appProfileStorage.getProfile !== 'function') {
            return;
          }

          try {
            // Check if AppProfile exists, create if not
            const existingProfile = await state.appProfileStorage.getProfile(profileId);
            if (!existingProfile) {
              const newAppProfile = await state.appProfileStorage.createProfile(profileId, accountId);
            }

            // Sync local profile data with AppProfile if needed
            // Note: always run PHI update regardless of whether the profile was just created or already existed
            const profile = state.getProfileById(profileId);
            if (profile && profile.name && profile.name !== 'Unknown') {
              const fallbackNameParts = (profile.name || '').trim().split(/\s+/).filter(Boolean);
              // Update PHI data from local profile
              await state.appProfileStorage.updatePHI(profileId, {
                dname: profile.name || '',
                fname: (profile.fname || '').trim() || fallbackNameParts[0] || '',
                lname: (profile.lname || '').trim() || fallbackNameParts.slice(1).join(' '),
                email: profile.email || '',
                dob: profile.dob,
                gender: profile.gender || '',
              });
              console.log('📋 Synced profile PHI data:', profileId);
            } else if (profile) {
              console.log('📋 Skipping PHI sync — profile name is empty or placeholder:', profile.name);
            }
          } catch (error) {
            console.error('Failed to sync profile with AppProfile:', error);
          }
        },

        updateAppProfilePHI: async (profileId: string, phi: Partial<AppProfilePHIDto>) => {
          const state = get();
          if (!state.appProfileStorage) {
            throw new Error('AppProfile storage not initialized');
          }

          try {
            await state.appProfileStorage.updatePHI(profileId, phi);
            console.log('📋 Updated AppProfile PHI:', profileId);
          } catch (error) {
            console.error('Failed to update AppProfile PHI:', error);
            throw error;
          }
        },

        updateAppProfileMilestone: async (profileId: string, name: string, value: string) => {
          const state = get();
          if (!state.appProfileStorage) {
            throw new Error('AppProfile storage not initialized');
          }

          try {
            await state.appProfileStorage.updateMilestone(profileId, name, value);
            console.log('📋 Updated AppProfile milestone:', profileId, { name, value });
          } catch (error) {
            console.error('Failed to update AppProfile milestone:', error);
            throw error;
          }
        },

        updateAppProfileConcern: async (profileId: string, name: string, value: string) => {
          const state = get();
          if (!state.appProfileStorage) {
            throw new Error('AppProfile storage not initialized');
          }

          try {
            await state.appProfileStorage.updateConcern(profileId, name, value);
            console.log('📋 Updated AppProfile concern:', profileId, { name, value });
          } catch (error) {
            console.error('Failed to update AppProfile concern:', error);
            throw error;
          }
        },

        getAppProfile: async (profileId: string) => {
          const state = get();
          if (!state.appProfileStorage) {
            console.warn('AppProfile storage not initialized');
            return null;
          }

          try {
            return await state.appProfileStorage.getProfile(profileId);
          } catch (error) {
            console.error('Failed to get AppProfile:', error);
            return null;
          }
        },

        getAllAppProfiles: async () => {
          const state = get();
          if (!state.appProfileStorage) {
            console.warn('AppProfile storage not initialized');
            return [];
          }

          try {
            return await state.appProfileStorage.getAllProfiles();
          } catch (error) {
            console.error('Failed to get all AppProfiles:', error);
            return [];
          }
        },

        // Backend sync methods for automatic profile synchronization
        syncProfileToBackend: async (profileId: string): Promise<boolean> => {
          console.log('📤 syncProfileToBackend: Starting sync for profile:', profileId);
          
          const state = get();
          const profile = state.getProfileById(profileId);
          if (!profile) {
            console.error('📤 syncProfileToBackend: Profile not found:', profileId);
            return false;
          }

          try {
            // Get the userStore instance to check authentication and get API service
            const userStore = useUserStore.getState();
            
            // First attempt to refresh authentication if it seems stale
            if (userStore.session.isAuthenticated !== true && userStore.session.user?.authToken) {
              console.log('📤 syncProfileToBackend: isAuthenticated not set, attempting to refresh authentication...');
              try {
                const refreshed = await userStore.refreshAuth();
                if (refreshed) {
                  console.log('📤 syncProfileToBackend: Authentication refreshed successfully');
                }
              } catch (error) {
                console.warn('📤 syncProfileToBackend: Authentication refresh failed:', error);
              }
            }
            
            // Check if user is authenticated and session is valid
            // Use more robust authentication check
            const isAuthenticated = userStore.session.isAuthenticated === true || 
              (userStore.session.user && userStore.session.user.authToken && userStore.isSessionValid());
              
            if (!isAuthenticated) {
              console.log('📤 syncProfileToBackend: Not authenticated or session invalid, attempting auto-login...');
              
              // Try to refresh authentication first
              const refreshed = await userStore.refreshAuth();
              if (!refreshed) {
                // If simple refresh fails, still proceed to API call - the 401 retry flow will handle refresh token
                console.log('📤 syncProfileToBackend: Simple refresh failed, will proceed to API call and let 401 retry flow handle it');
                
                // Only bail out if we're in a completely broken state (no stored credentials at all)
                const isDev = typeof __DEV__ !== 'undefined'
                  ? __DEV__
                  : (globalThis as any).process?.env?.NODE_ENV === 'development';
                const userHasPreviouslyLoggedIn = await userStore.checkStoredAuth() || 
                  (await userStore.getStoredItem?.('user_email')) || 
                  (await userStore.getStoredItem?.('auth_timestamp'));
                  
                // Check if we have any refresh token available for the 401 retry flow
                const globalStorage = (global as any).__childNavStorage;
                const storage = globalStorage;
                const hasRefreshToken = storage ? !!(await storage.getItem('refresh_token')) : false;
                
                if (!userHasPreviouslyLoggedIn && !hasRefreshToken) {
                  console.log('📤 syncProfileToBackend: No previous login and no refresh token - app should operate without account. Marking profile as dirty for later sync.');
                  get().markProfileDirty(profileId, 'No account setup - will sync when user logs in');
                  return false;
                }
                
                // Otherwise, proceed with API call - the 401 retry flow will handle the refresh token
                console.log('📤 syncProfileToBackend: Found refresh token or previous login, proceeding with API call...');
              }
            }

            // Get API service
            const apiService = userStore.getApiService();
            if (!apiService) {
              console.error('📤 syncProfileToBackend: API service not available, marking profile as dirty');
              get().markProfileDirty(profileId, 'API service not available');
              return false;
            }

            // Safety guard: refuse to overwrite existing server data with an empty milestonestate
            // only while profile-store rehydration is still in progress.
            // After rehydration completes, an empty milestone array can be a legitimate state
            // (for example, debug profiles or intentionally reset/deleted milestone data).
            if (!profileStoreRehydrated && profile.milestonestate.length === 0 && profile.lastSyncDate) {
              console.warn(
                '⚠️ syncProfileToBackend: Refusing to upload empty milestonestate for previously-synced profile.',
                'This normally means the store has not finished rehydrating.',
                { profileId, lastSyncDate: profile.lastSyncDate, profileStoreRehydrated }
              );
              get().markProfileDirty(profileId, 'Upload blocked: empty milestonestate on previously-synced profile — will retry after rehydration');
              return false;
            }

            // Convert profile to AppProfileDto format
            const accountId = userStore.session?.familyAccount?.familyId || 'default-account';
            const appProfileDto = convertChildProfileToAppProfileDto(profile, accountId);

            console.log('📤 syncProfileToBackend: Converted profile to AppProfileDto:', {
              profileId: appProfileDto.profileId,
              accountId: appProfileDto.accountId,
              milestonesCount: appProfileDto.milestones.length,
              profilePrescriptionEvents: ((profile as any).prescriptiontrack?.events ?? (profile as any).prescriptiontrack?.Events ?? []).length,
              profileReferralEvents: ((profile as any).referraltrack?.events ?? (profile as any).referraltrack?.Events ?? []).length,
              prescriptionEvents: appProfileDto.prescriptions?.events?.length ?? 0,
              referralEvents: appProfileDto.referrals?.events?.length ?? 0,
            });

            // Create service request
            const request: ServiceRequestDto = {
              RequestType: 'profile_put',
              Payload: JSON.stringify(appProfileDto)
            };
            
            // Store request details for potential retry
            const originalRequest = {
              requestDto: request,
              profileData: appProfileDto,
              accountId
            };

            // Send to backend  
            console.log('📤 syncProfileToBackend: Sending profile_put request...');
            const response = await apiService.processServiceRequestWithRefresh(request, 'sync', {
              userStore,
              storage: (global as any).__childNavStorage,
              checkNetworkConnectivity: get().checkNetworkConnectivity
            });

            if (response?.success) {
              console.log('✅ syncProfileToBackend: Profile sync successful for:', profileId);
              
              // Update profile to mark as synced and clear dirty flag
              const now = new Date().toISOString();
              get().updateProfile(profileId, {
                lastSyncDate: now,
                isDirty: false,
                lastSyncAttempt: now,
                syncFailureReason: undefined
              });
              
              return true;
            } else {
              console.error('❌ syncProfileToBackend: Backend rejected sync:', response?.message);
              get().markProfileDirty(profileId, `Backend error: ${response?.message || 'Unknown error'}`);
              return false;
            }

          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            console.error('❌ syncProfileToBackend: Sync error:', errorMessage);
            
            // Check if this is an authentication-related error from the retry logic
            const isAuthError = errorMessage.includes('Session expired') ||
                              errorMessage.includes('manual login required') ||
                              errorMessage.includes('Auth retry failed') ||
                              errorMessage.includes('No refresh token') ||
                              errorMessage.includes('re-auth process failed');
            
            if (isAuthError) {
              get().markProfileDirty(profileId, errorMessage);
            } else {
              get().markProfileDirty(profileId, `Network/sync error: ${errorMessage}`);
            }
            
            return false;
          }
        },

        checkNetworkConnectivity: async (): Promise<boolean> => {
          try {
            // Try a simple network test - attempt to resolve a DNS or make a basic request
            // This is a basic implementation that can be enhanced
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
            
            try {
              // Try to fetch a simple endpoint or use a basic connectivity test
              const response = await fetch('https://www.google.com/favicon.ico', {
                method: 'HEAD',
                signal: controller.signal,
                mode: 'no-cors'
              });
              clearTimeout(timeoutId);
              return true;
            } catch (fetchError) {
              clearTimeout(timeoutId);
              
              // Fallback: try local API service if available
              try {
                const userStore = useUserStore.getState();
                const apiService = userStore.getApiService();
                if (apiService) {
                  // Try a simple ping or health check if available
                  return true; // If we have apiService, assume network is available
                }
                return false;
              } catch {
                return false;
              }
            }
          } catch (error) {
            console.log('🌐 checkNetworkConnectivity: Network check failed:', error);
            return false;
          }
        },

        markProfileDirty: (profileId: string, reason?: string) => {
          console.log('🔴 markProfileDirty: Marking profile as dirty:', profileId, reason);
          const now = new Date().toISOString();
          get().updateProfile(profileId, {
            isDirty: true,
            lastSyncAttempt: now,
            syncFailureReason: reason || 'Unknown sync failure'
          });
        },

        retryDirtyProfiles: async () => {
          console.log('🔄 retryDirtyProfiles: Starting retry for dirty profiles...');
          const dirtyProfiles = get().getDirtyProfiles();
          
          if (dirtyProfiles.length === 0) {
            console.log('🔄 retryDirtyProfiles: No dirty profiles found');
            return;
          }

          console.log(`🔄 retryDirtyProfiles: Found ${dirtyProfiles.length} dirty profiles to retry`);
          
          for (const profile of dirtyProfiles) {
            console.log(`🔄 retryDirtyProfiles: Retrying sync for profile: ${profile.name} (${profile.id})`);
            await get().syncProfileToBackend(profile.id);
            
            // Add small delay between retries to avoid overwhelming the server
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        },

        getDirtyProfiles: () => {
          const state = get();
          return state.profiles.filter(profile => profile.isDirty === true);
        },
        
        reset: () => set({
          profiles: [],
          activeProfileId: null,
          developmentAges: {},
          appProfileStorage: null,
        }),
        
        // Utility methods for debugging and testing persistence
        debugPersistence: async () => {
          try {
            const storedData = await storage.getItem('child-nav-profiles');
            console.log('📋 debugPersistence: Raw stored data:', storedData ? 'found' : 'not found');
            if (storedData) {
              const parsed = JSON.parse(storedData);
              console.log('📋 debugPersistence: Parsed data:', {
                profilesCount: parsed.profiles?.length || 0,
                activeProfileId: parsed.activeProfileId,
                developmentAgesCount: Object.keys(parsed.developmentAges || {}).length
              });
              return parsed;
            }
            return null;
          } catch (error) {
            console.error('📋 debugPersistence: Error:', error);
            return null;
          }
        },
        
        forceRehydrate: async () => {
          console.log('📋 forceRehydrate: Manually triggering rehydration...');
          try {
            const storedData = await storage.getItem('child-nav-profiles');
            if (storedData) {
              const parsed = JSON.parse(storedData);
              
              // Migrate profiles to ensure compatibility with new fields
              let migratedProfiles = parsed.profiles || [];
              if (migratedProfiles.length > 0) {
                console.log('📋 forceRehydrate: Migrating', migratedProfiles.length, 'profiles for compatibility');
                migratedProfiles = migratedProfiles.map((profile: any) => {
                  try {
                    return migrateProfileData(profile);
                  } catch (migrationError) {
                    console.error('📋 forceRehydrate: Migration error for profile:', profile.id, migrationError);
                    // Return the profile as-is if migration fails
                    return profile;
                  }
                });
              }
              
              set({
                profiles: migratedProfiles,
                activeProfileId: parsed.activeProfileId || null,
                developmentAges: parsed.developmentAges || {},
                appProfileStorage: get().appProfileStorage // Keep existing appProfileStorage
              });
              console.log('📋 forceRehydrate: Successfully rehydrated', migratedProfiles.length, 'profiles');
              return true;
            }
            console.log('📋 forceRehydrate: No stored data to rehydrate');
            return false;
          } catch (error) {
            console.error('📋 forceRehydrate: Error:', error);
            return false;
          }
        },
      }),
      {
        name: 'child-nav-profiles',
        storage: zustandStorage,
        partialize: (state) => ({
          profiles: state.profiles,
          activeProfileId: state.activeProfileId,
          developmentAges: state.developmentAges,
          // Exclude appProfileStorage from persistence as it's a class instance
        }),
        onRehydrateStorage: () => {
          return (state, error) => {
            if (error) {
              console.error('📋 Profile Store: Rehydration failed:', error);
            } else {
              console.log('📋 Profile Store: Rehydration completed successfully');
              if (state) {
                // Migrate profiles to ensure compatibility with new fields
                if (state.profiles && state.profiles.length > 0) {
                  console.log('📋 Profile Store: Migrating', state.profiles.length, 'profiles for compatibility');
                  state.profiles = state.profiles.map((profile: any) => {
                    try {
                      return migrateProfileData(profile);
                    } catch (migrationError) {
                      console.error('📋 Profile Store: Migration error for profile:', profile.id, migrationError);
                      // Return the profile as-is if migration fails
                      return profile;
                    }
                  });
                }
                
                console.log('📋 Profile Store: Rehydrated', state.profiles?.length || 0, 'profiles');
                console.log('📋 Profile Store: Active profile ID:', state.activeProfileId);
                
                // Ensure appProfileStorage is properly reinitialized after rehydration
                if (!state.appProfileStorage && (global as any).__childNavStorage) {
                  console.log('📋 Profile Store: Reinitializing AppProfileStorage after rehydration');
                  try {
                    state.initializeAppProfileStorage((global as any).__childNavStorage);
                  } catch (initError) {
                    console.error('📋 Profile Store: Failed to reinitialize AppProfileStorage:', initError);
                  }
                }
              }
            }
          };
        },
      }
    )
  );

  // NOTE: AppProfileStorage initialization is intentionally deferred to
  // onRehydrateStorage (below).  Calling initializeAppProfileStorage() here —
  // before Zustand rehydration completes — causes the persist middleware to fire
  // set({ appProfileStorage }) with profiles: [] (the initial empty state), which
  // overwrites the real persisted profile data in 'child-nav-profiles'.
  // On the next cold start that empty write is read back, making profiles appear
  // to be lost.  The onRehydrateStorage callback is the safe place to do this
  // because rehydration has already merged the stored profiles into the store by
  // the time that callback fires.
  console.log('📋 Profile Store: AppProfileStorage init deferred to onRehydrateStorage');

  // Verify persistence is working
  console.log('📋 Profile Store: Persistence initialized');
  console.log('📋 Profile Store: Storage adapter available:', !!storage);

  // Diagnostic: peek at what is stored WITHOUT triggering a set() / persist write.
  // (This is read-only — it does NOT affect the store or the stored data.)
  storage.getItem('child-nav-profiles').then(stored => {
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // Zustand 5 persist wraps state as { state: {...}, version: 0 }
        const profiles = parsed?.state?.profiles ?? parsed?.profiles ?? [];
        console.log('📋 Profile Store: Found persisted profiles:', profiles.length);
      } catch (error) {
        console.warn('📋 Profile Store: Error parsing persisted data:', error);
      }
    } else {
      console.log('📋 Profile Store: No persisted data found (fresh start)');
    }
  }).catch(error => {
    console.error('📋 Profile Store: Error checking persisted data:', error);
  });

  // Store global references for development profile initialization
  (global as any).__userStore = useUserStore;
  (global as any).__childNavStorage = storage;

  // Rehydration-complete flag — set to true inside onRehydrateStorage so that
  // checkInitialDirtyProfiles can wait for actual data before attempting any sync.
  let profileStoreRehydrated = false;

  // Patch the already-registered onRehydrateStorage result handler to set the flag.
  // Zustand persist fires the callback immediately if data is already loaded, so
  // we also check whether profiles exist right now (fast-path for re-init).
  if (useProfileStore.getState().profiles.length > 0) {
    // Data was already present when the store was created (e.g. re-initialization path)
    profileStoreRehydrated = true;
    console.log('🔄 profileStoreRehydrated set immediately (profiles already present)');
  } else {
    // Subscribe once to the persist rehydration finish event
    const unsubFinishHydration = useProfileStore.persist?.onFinishHydration?.((state: any) => {
      profileStoreRehydrated = true;
      console.log('🔄 profileStoreRehydrated: onFinishHydration fired');
      unsubFinishHydration?.();
    });
  }

  // Check for dirty profiles on initialization and retry if user is authenticated AND has previously logged in
  const checkInitialDirtyProfiles = async () => {
    console.log('🔄 checkInitialDirtyProfiles: Checking for dirty profiles on store initialization...');
    
    const userStore = useUserStore.getState();
    const profileStore = useProfileStore.getState();
    
    // Use robust authentication check
    const isAuthenticated = userStore.session.isAuthenticated === true || 
      (userStore.session.user && userStore.session.user.authToken && userStore.isSessionValid());
    
    // Also check if user has previously logged in (evidence of account setup)
    const userHasPreviouslyLoggedIn = await userStore.checkStoredAuth() || 
      (storage && await storage.getItem('user_email')) || 
      (storage && await storage.getItem('auth_timestamp'));
    
    if (isAuthenticated && userHasPreviouslyLoggedIn) {
      const dirtyProfiles = profileStore.getDirtyProfiles();
      if (dirtyProfiles.length > 0) {
        console.log(`🔄 Found ${dirtyProfiles.length} dirty profiles on initialization — auto-sync disabled. Profiles sync only on explicit user action.`);
      } else {
        console.log('🔄 No dirty profiles found on initialization');
      }
    } else {
      console.log('🔄 User not authenticated or no previous login detected on initialization, skipping dirty profile retry');
      
      if (!userHasPreviouslyLoggedIn) {
        const dirtyProfiles = profileStore.getDirtyProfiles();
        if (dirtyProfiles.length > 0) {
          console.log(`🔄 Found ${dirtyProfiles.length} dirty profiles but no previous login - profiles will sync when user sets up account`);
        }
      }
    }
  };

  // Wait for store rehydration before attempting dirty-profile retry.
  // Poll up to 10 seconds in 200 ms increments, then give up gracefully.
  const waitForRehydrationThenCheck = async () => {
    const MAX_WAIT_MS = 10000;
    const POLL_INTERVAL_MS = 200;
    let elapsed = 0;

    while (!profileStoreRehydrated && elapsed < MAX_WAIT_MS) {
      await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
      elapsed += POLL_INTERVAL_MS;
    }

    if (!profileStoreRehydrated) {
      console.warn('🔄 Store rehydration did not complete within 10 s — skipping initial dirty-profile retry to avoid uploading stale data');
      return;
    }

    console.log(`🔄 Store rehydrated after ~${elapsed} ms — running checkInitialDirtyProfiles`);
    checkInitialDirtyProfiles().catch(error => {
      console.error('🔄 checkInitialDirtyProfiles failed:', error);
    });
  };

  waitForRehydrationThenCheck().catch(error => {
    console.error('🔄 waitForRehydrationThenCheck failed:', error);
  });

  // Add session state listener to retry dirty profiles when authentication becomes available
  let previousAuthState = useUserStore.getState().session.isAuthenticated;
  let previousSessionValid = useUserStore.getState().isSessionValid();
  
  useUserStore.subscribe((state) => {
    const currentAuthState = state.session.isAuthenticated;
    const currentSessionValid = useUserStore.getState().isSessionValid();
    
    // More robust authentication check for session monitoring
    const isCurrentlyAuthenticated = currentAuthState === true || 
      (state.session.user && state.session.user.authToken && currentSessionValid);
    const wasPreviouslyAuthenticated = previousAuthState === true || 
      (previousAuthState && previousSessionValid);
    
    // Check if user just became authenticated or session became valid
    const justAuthenticated = !wasPreviouslyAuthenticated && isCurrentlyAuthenticated;
    const sessionJustBecameValid = !previousSessionValid && currentSessionValid;
    
    if (justAuthenticated || sessionJustBecameValid) {
      console.log('🔄 Session state changed: User authenticated, checking for dirty profiles...');
      
      // Only attempt auto-sync if this appears to be a legitimate login (not just auto-restore)
      // Check if this is from a real user login by looking for recent activity
      const isRealUserLogin = state.session.user && (
        Date.now() - new Date(state.session.sessionStartTime || 0).getTime() < 30000 || // Session started within last 30 seconds
        Date.now() - new Date(state.session.lastActivityTime || 0).getTime() < 30000   // Recent activity
      );
      
      if (isRealUserLogin) {
        // Get dirty profiles and attempt to sync them
        const profileStore = useProfileStore.getState();
        const dirtyProfiles = profileStore.getDirtyProfiles();
        
        if (dirtyProfiles.length > 0) {
          console.log(`🔄 Found ${dirtyProfiles.length} dirty profiles after user login — auto-sync disabled. Profiles sync only on explicit user action.`);
        } else {
          console.log('🔄 No dirty profiles found to sync');
        }
      } else {
        console.log('🔄 Session change detected but not from recent user activity - skipping auto-sync');
      }
    }
    
    // Update previous state for next comparison
    previousAuthState = currentAuthState;
    previousSessionValid = currentSessionValid;
  });

  // Add a configuration function to set the API service
  const setApiService = (apiService: import('../services/api').ApiService) => {
    useUserStore.getState().setApiService(apiService);
  };

  // Helper method removed - development profiles no longer auto-created

  // Initialize app with proper authentication sequence
  const initializeApp = async () => {
    try {

      // Step 1: Restore in-memory auth session from localStorage — runs in ALL environments.
      // After a sign-out + sign-in cycle the fresh store instance starts with no in-memory
      // session even though localStorage still holds a valid token.
      // We always attempt restore regardless of isSessionValid() to ensure the apiService
      // token is set even when cached stores (isStoreInitialized=true) are returned.
      const userState = useUserStore.getState();
      const sessionAlreadyValid = userState.isSessionValid();
      if (!sessionAlreadyValid) {
        try {
          // checkStoredAuth is the more complete restore: sets apiService token,
          // rebuilds the full AppUser + FamilyAccount objects, and flips isAuthenticated.
          let restored = false;
          if (userState.checkStoredAuth) {
            restored = await userState.checkStoredAuth();
          }
          if (!restored && userState.refreshAuth) {
            // Fallback: refreshAuth only needs userSession + auth_token (less strict).
            const refreshed = await userState.refreshAuth();
          }
        } catch (error) {
          console.warn('📋 App: Auth restore failed, continuing with initialization:', error);
        }
      } else {
        // Session is "valid" in Zustand but apiService.authToken might be null if
        // sign-out called setAuthToken(null). Re-sync the token from the session.
        const storedToken = await (userState as any).getStoredItem?.('auth_token');
        const sessionToken = userState.session?.user?.authToken;
        const token = storedToken || sessionToken;
        if (token && typeof useUserStore.getState().setApiService === 'function') {
          // Best-effort: set the token directly via the config reference.
          const apiSvc = useUserStore.getState().getApiService?.();
          if (apiSvc) {
            apiSvc.setAuthToken(token);
          }
        }
      }

    } catch (error) {
      console.error('📋 App: Initialization failed:', error);
    }
  };

  const stores = {
    useUserStore,
    useMilestonesStore,
    useConcernsStore,
    useWellVisitsStore,
    useActionsStore,
    useProfileStore,
    useContentStore,
    setApiService,
    initializeApp, // Export for proper app initialization
    
    // Add method to force re-hydration when storage is corrected
    forceProfileRehydration: async () => {
      console.log('🔄 Forcing profile store re-hydration...');
      try {
        // Access the persist API to manually rehydrate
        if (useProfileStore.persist && useProfileStore.persist.rehydrate) {
          await useProfileStore.persist.rehydrate();
          console.log('✅ Profile store re-hydration completed');
        } else {
          console.log('⚠️ Profile store persist API not available');
        }
      } catch (error) {
        console.error('❌ Profile store re-hydration failed:', error);
      }
    },
  };
  
  // Store globally and mark as initialized
  (global as any).__childNavStorage = storage;
  (global as any).__childNavStores = stores;
  
  // CRITICAL: Also expose profile store under the expected global property names
  // that DebugView and ApiService are looking for
  (global as any).__profileStore = useProfileStore;
  if (typeof window !== 'undefined') {
    (window as any).__profileStore = useProfileStore;
  }
  
  isStoreInitialized = true;
  
  return stores;
};

/**
 * Reset the store-initialization guard so that the NEXT call to
 * createStoresWithStorage creates a completely fresh store tree.
 *
 * Call this during sign-out so that after sign-in, ChildNavWeb gets
 * brand-new Zustand stores whose session starts in the default
 * (unauthenticated) state.  Without this reset, the stale cached session
 * from the previous sign-in would persist across sign-out/sign-in cycles
 * and cause isSessionValid() to return true, skipping refreshAuth and
 * leaving the API service without a valid token.
 */
export const resetStoreInitialization = (): void => {
  console.log('🔄 resetStoreInitialization: clearing cached stores for next sign-in');
  isStoreInitialized = false;
  (global as any).__childNavStores = null;
  (global as any).__childNavStorage = null;
  (global as any).__userStore = null;
  (global as any).__profileStore = null;
  if (typeof window !== 'undefined') {
    (window as any).__profileStore = null;
  }
};